import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Upload,
  Button,
  Table,
  Tag,
  Alert,
  Space,
  Modal as ModalAntd,
  notification,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import Loader from "../../components/Loader/Loader.js";
import ApiKeyPanel from "../../components/ApiKeyPanel/ApiKeyPanel.jsx";
import { useAuth } from "../../hooks";
import { BACKEND_URL } from "../../config/config";

// Same columns the CRM admin's own CSV template uses (see
// jquery.account.importBatches.js on the CRM side) - only "Y" values in the
// role/email columns are honored, everything else is left blank.
//
// "action" is optional per row - blank/omitted defaults to "add". "remove"
// unlinks an existing contact from an existing account and only needs
// accountName/street/state + contactEmail - everything else can be left
// blank, so you don't have to resend a contact's full details just to
// remove them from an account.
const TEMPLATE_HEADERS = [
  "action", "accountName", "street", "city", "zip", "state",
  "firstName", "lastName", "title", "contactMobile", "contactEmail",
  "mainPhone", "website", "contactPhone",
  "mainContact", "accountPayable",
  "emailProposal", "emailAFU", "emailDispatch", "emailInvoice",
  "emailOpportunityNotification", "SelfInspection",
];

function csvField(value) {
  const text = String(value == null ? "" : value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function downloadTemplate() {
  const rows = [
    TEMPLATE_HEADERS,
    ["add", "Acme Properties LLC", "123 Main St", "Springfield", "62701", "IL", "Jane", "Doe", "Property Manager", "(555) 123-4567", "jane.doe@acmeproperties.com", "(555) 987-6543", "www.acmeproperties.com", "", "Y", "", "", "Y", "", "", "", ""],
    ["remove", "Acme Properties LLC", "123 Main St", "Springfield", "62701", "IL", "", "", "", "", "old.contact@acmeproperties.com", "", "", "", "", "", "", "", "", "", "", ""],
  ];
  const lines = rows.map((row) => row.map(csvField).join(",")).join("\n");
  const blob = new Blob([lines], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "import-contacts-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const STATUS_COLORS = {
  pending: "gold",
  partial: "orange",
  approved: "green",
  rejected: "red",
};

// One "field: value" line, bold when the value actually changed vs. what's
// on file today - mirrors importDiffFieldLine in the CRM's
// jquery.account.importBatches.js so both review UIs read the same way.
function FieldLine({ label, after, before, isNew }) {
  if (!after) return null;
  const changed = !isNew && before && after !== before;
  return (
    <div style={{ fontSize: 12 }}>
      {label}: {changed ? <b>{after}</b> : after}
    </div>
  );
}

function AccountDiff({ diff, linkAction }) {
  if (!diff) return <span style={{ color: "#999" }}>-</span>;
  const isRemove = linkAction === "remove";
  const isNew = diff.action === "create";
  const after = isRemove ? diff.before || {} : diff.after || {};
  const before = isRemove ? {} : diff.before || {};
  return (
    <div>
      <Tag color={isRemove ? "red" : isNew ? "green" : "blue"}>
        {isRemove ? "UNLINK" : isNew ? "NEW" : "MATCH"}
      </Tag>
      <FieldLine label="Name" after={after.accountName} before={before.accountName} isNew={isRemove || isNew} />
      <FieldLine label="Street" after={after.street} before={before.street} isNew={isRemove || isNew} />
      <FieldLine label="City" after={after.city} before={before.city} isNew={isRemove || isNew} />
      <FieldLine label="State" after={after.state} before={before.state} isNew={isRemove || isNew} />
      <FieldLine label="Zip" after={after.zip} before={before.zip} isNew={isRemove || isNew} />
      <FieldLine label="Phone" after={after.mainPhone} before={before.mainPhone} isNew={isRemove || isNew} />
      <FieldLine label="Website" after={after.website} before={before.website} isNew={isRemove || isNew} />
    </div>
  );
}

function ContactDiff({ diff, linkAction }) {
  if (!diff) return <span style={{ color: "#999" }}>-</span>;
  const isRemove = linkAction === "remove";
  const isNew = diff.action === "create";
  const after = isRemove ? diff.before || {} : diff.after || {};
  const before = isRemove ? {} : diff.before || {};
  const afterName = `${after.firstName || ""} ${after.lastName || ""}`.trim();
  const beforeName = `${before.firstName || ""} ${before.lastName || ""}`.trim();
  return (
    <div>
      <Tag color={isRemove ? "red" : isNew ? "green" : "blue"}>
        {isRemove ? "UNLINK" : isNew ? "NEW" : "MATCH"}
      </Tag>
      <FieldLine label="Name" after={afterName} before={beforeName} isNew={isRemove || isNew} />
      <FieldLine label="Title" after={after.title} before={before.title} isNew={isRemove || isNew} />
      <FieldLine label="Mobile" after={after.phone?.mobile} before={before.phone?.mobile} isNew={isRemove || isNew} />
      <FieldLine label="Phone" after={after.phone?.main} before={before.phone?.main} isNew={isRemove || isNew} />
      <FieldLine label="Email" after={after.email?.main} before={before.email?.main} isNew={isRemove || isNew} />
    </div>
  );
}

function RolesCell({ row }) {
  if (row.linkAction === "remove") {
    return row.alreadyUnlinked ? (
      <span style={{ color: "#999" }}>Already not linked (no-op)</span>
    ) : (
      <span style={{ color: "#c46a69", fontStyle: "italic" }}>Unlink contact from this account</span>
    );
  }
  const flags = row.roleFlags || {};
  const parts = [];
  if (flags.mainContact) parts.push("Main Contact");
  if (flags.accountPayable) parts.push("Accounts Payable");
  if (flags.selfInspection) parts.push("Self-Inspection");
  (row.emailModules || []).forEach((m) => parts.push(`Email: ${m}`));
  if (!parts.length) return <span style={{ color: "#999" }}>-</span>;
  return (
    <div>
      {parts.map((p) => (
        <div key={p} style={{ fontSize: 12 }}>{p}</div>
      ))}
    </div>
  );
}

function RowDiffTable({ rows }) {
  const columns = [
    { title: "Row", dataIndex: "csvRow", width: 50 },
    {
      title: "Account",
      render: (_, row) => (row.parseError ? "-" : <AccountDiff diff={row.account} linkAction={row.linkAction} />),
    },
    {
      title: "Contact",
      render: (_, row) => (row.parseError ? "-" : <ContactDiff diff={row.contact} linkAction={row.linkAction} />),
    },
    {
      title: "Roles / Email",
      render: (_, row) => (row.parseError ? "-" : <RolesCell row={row} />),
    },
    {
      title: "Status",
      render: (_, row) =>
        row.parseError ? (
          <Tag color="red">{row.parseError}</Tag>
        ) : (
          <Tag color="green">OK - ready for review</Tag>
        ),
    },
  ];

  return (
    <Table
      rowKey="csvRow"
      columns={columns}
      dataSource={rows || []}
      pagination={false}
      size="small"
    />
  );
}

export function ImportData() {
  const { user } = useAuth();
  const [fileList, setFileList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastBatch, setLastBatch] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [viewingBatch, setViewingBatch] = useState(null);

  const loadHistory = async () => {
    if (!user?._id) return;
    setIsLoadingHistory(true);
    const response = await axios
      .get(`${BACKEND_URL}/customers/importBatches`, {
        params: { contact_id: user._id },
      })
      .catch(() => null);
    setIsLoadingHistory(false);
    if (response?.data) setHistory(response.data);
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // preview=true never saves anything on the backend - it's a pure dry run
  // so the file can be checked (and re-uploaded to fix mistakes) before it's
  // actually filed as a pending import.
  const runImport = async (preview) => {
    if (!fileList.length) {
      notification.error({ message: "Pick a CSV file first." });
      return;
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("contact_id", user._id);
    formData.append("csvFile", fileList[0]);
    if (preview) formData.append("preview", "true");

    const response = await axios
      .post(`${BACKEND_URL}/customers/importBatches`, formData)
      .catch((err) => {
        notification.error({
          message: err?.response?.data?.message || "Unable to process this file.",
        });
        return null;
      });

    setIsSubmitting(false);
    if (!response?.data) return;

    setLastBatch(response.data);

    if (!preview) {
      notification.success({
        message:
          "Submitted for review. Our team will approve or reject each row before anything changes.",
      });
      setFileList([]);
      setLastBatch(null);
      loadHistory();
    }
  };

  const viewBatch = async (batchId) => {
    const response = await axios
      .get(`${BACKEND_URL}/customers/importBatches/${batchId}`, {
        params: { contact_id: user._id },
      })
      .catch(() => null);
    if (response?.data) setViewingBatch(response.data);
  };

  const historyColumns = [
    { title: "File", dataIndex: "fileName" },
    {
      title: "Uploaded",
      dataIndex: "created",
      render: (value) => dayjs(value).format("MM/DD/YYYY HH:mm"),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value) => <Tag color={STATUS_COLORS[value] || "default"}>{value}</Tag>,
    },
    {
      title: "Rows",
      render: (_, record) =>
        `${record.summary?.totalRows ?? 0} (${record.summary?.rowErrors ?? 0} errors)`,
    },
    {
      title: "",
      render: (_, record) => (
        <Button size="small" onClick={() => viewBatch(record._id)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", paddingTop: "1rem", gap: 16 }}>
      <Alert
        type="info"
        showIcon
        message="Uploading a CSV never updates your account directly - each row is checked and sent to our team for review before anything changes."
      />

      <ApiKeyPanel contactId={user?._id} />

      <Upload.Dragger
        accept=".csv"
        maxCount={1}
        fileList={fileList.map((f, i) => ({ uid: String(i), name: f.name, status: "done" }))}
        beforeUpload={(file) => {
          setFileList([file]);
          return false;
        }}
        onRemove={() => setFileList([])}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Drag & drop a CSV file here, or click to browse</p>
      </Upload.Dragger>

      <div style={{ display: "flex", gap: 8 }}>
        <Button type="primary" onClick={() => runImport(true)} loading={isSubmitting}>
          Preview Before/After
        </Button>
        <Button onClick={downloadTemplate}>Download CSV Template</Button>
      </div>

      {lastBatch ? (
        <div>
          <h4>
            Review before submitting - {lastBatch.summary?.totalRows} rows,{" "}
            {lastBatch.summary?.rowErrors} need fixing
          </h4>
          <RowDiffTable rows={lastBatch.rows} />
          <Space style={{ marginTop: 8 }}>
            <Button type="primary" onClick={() => runImport(false)} loading={isSubmitting}>
              Looks Good - Send for Review
            </Button>
            <Button onClick={() => { setLastBatch(null); setFileList([]); }}>
              Discard and Re-upload
            </Button>
          </Space>
        </div>
      ) : null}

      <h4>Import History</h4>
      {isLoadingHistory ? (
        <Loader />
      ) : (
        <Table rowKey="_id" columns={historyColumns} dataSource={history} pagination={false} size="small" />
      )}

      <ModalAntd
        title="Import Batch Detail"
        open={!!viewingBatch}
        onCancel={() => setViewingBatch(null)}
        footer={null}
        width={900}
      >
        {viewingBatch ? <RowDiffTable rows={viewingBatch.rows} /> : null}
      </ModalAntd>
    </div>
  );
}
